import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import * as TFF from '@tff/types/tff';
import { MetaValue, SsrDetails, PaxData } from '@tff/types/tff/tfa-amend-ancillary-service';
import { Journey } from '@tff/types/tff';
import { Box, Button, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import TFFDialog from '../../styled-components/TFFDialog';
import TFFTypography from '../../styled-components/TFFTypography';

import AncillaryAccordion from './AncillaryAccordion';
import { useAncillaryBooking } from '../../hooks/use-ancillary-booking';
import useStyles from './AccordionStyles';
import AncillaryCostTable from './AncillaryCostTable';
import { ConfirmationDialog } from '../AncillaryTable/ConfirmationDialog';
import { useTypedSelector } from '../../reducers';
import localeFormat from '../../util/localeFormat';
import { mergeBXObjects } from './Utils/AncillariesPage/mergeBXObjects';
import { formSsrs } from './Utils/AncillariesPage/formSsrs';
import { updateSsrCode } from './Utils/AncillariesPage/updateSsrCode';
import { groupByType } from './Utils/AncillariesPage/groupByType';
import { updateGroupedRows } from './Utils/AncillariesPage/updateGroupedRows';
import { mergeByType } from './Utils/AncillariesPage/mergeByType';
import CostOverview from '../CostOverview';
import { generatePaxDataForAmendRequest } from './Utils/AncillariesPage/generatePaxDataForAmendRequest';
import { useAmendBooking } from '../../hooks/use-amend-booking';
import { ValidationDialog } from './ValidationDialog';
import { resetSsrs } from './Utils/AncillariesPage/resetSsrs';
import { addSsrsToAdd } from './Utils/AncillariesPage/addSsrsToAdd';
import { removeSsrsToAdd } from './Utils/AncillariesPage/removeSsrsToAdd';
import { AmendDialog } from './AmendDialog';
import {
  addMissingAncillariesToMetaValues,
  getJourneysOnd,
  getMissingAncillaries,
  MissingAncillaries,
} from './Utils/AncillariesPage/missingAncillaries';
import { checkDirection } from './Utils/AncillariesPage/checkDirection';
import { getNewAddedSsrs } from './Utils/AncillaryCostTable/getNewAddedSsrs';
import { SsrsByPax, SurchargeType } from '../../types/ancillaryTypes';
import { RecordLocator } from '@tff/types';
import { useBookingContext } from '../../hooks/use-booking-context';
import { useMessages } from '../../hooks/use-messages';

interface props {
  flightDetails: TFF.FlightDetails;
  midocoOrderNo?: number;
  rebooking?: boolean;
  showCostTable?: boolean;
  setShowCostTable?: (value: boolean) => void;
  rebookOffers?: any;
  ancillarySeats?: any;
  payload?: any;
  ancillaryRequest?: boolean;
  handleAmendBookingConfirm?: (value: PaxData[], serviceFee: number) => void;
  sessionId?: string;
  displayRebookConfirmation?: boolean;
  rebookingResponse?: any;
  setAncillaryData?: (value) => void;
  newOnds?: {
    Outbound: string;
    Inbound: string;
  };
  tfmPnr?: string;
  setAncillaryId?: (value: string) => void;
  fareFamily?: string[] | undefined;
  amendBalanceDue?: number;
  setAncillariesPage?: (value: boolean) => void;
  displayTable: boolean;
  setDisplayTable: (value: boolean) => void;
  isNskBooking: boolean | undefined;
}

interface AncillariesAccordion {
  type: string;
  ancillaries: MetaValue[];
}

interface GroupedAncillaries {
  type: string;
  ancillaries: { outbound: MetaValue[]; inbound: MetaValue[] };
  matchedPassengers: {};
}

const AncillariesPage: React.FC<props> = ({
  flightDetails,
  midocoOrderNo,
  rebooking,
  showCostTable,
  setShowCostTable,
  rebookOffers,
  payload,
  ancillaryRequest,
  handleAmendBookingConfirm,
  sessionId,
  displayRebookConfirmation,
  setAncillaryData,
  newOnds,
  tfmPnr,
  setAncillaryId,
  fareFamily,
  ancillarySeats,
  amendBalanceDue,
  setAncillariesPage,
  displayTable,
  setDisplayTable,
  isNskBooking,
}) => {
  const { locale } = useTypedSelector(({ settings }) => settings.locale);
  const intl = useIntl();
  const { currency, recordLocator } = useBookingContext();
  const { sendMessage } = useMessages();

  const journeys: TFF.Journey[] = flightDetails.OrderDetails.Journeys;

  const { setAmendBookingRequest, amendResponse, amendStatus } = useAmendBooking();
  const [groupedRows, setGroupedRows] = useState<GroupedAncillaries[]>([]);
  const [outboundRows, setOutboundRows] = useState<AncillariesAccordion[]>([]);
  const [inboundRows, setInboundRows] = useState<AncillariesAccordion[]>([]);
  const [outboundRaw, setOutboundRaw] = useState<MetaValue[]>([]);
  const [inboundRaw, setInboundRaw] = useState<MetaValue[]>([]);
  const [ssrErrors, setSsrErrors] = useState<string>();
  const [validationDialog, setValidationDialog] = useState<boolean>(false);

  // The adjustableSurcharge is set through AncillaryCostTable to be sent in the final request (confirmation of amend)
  // In the future we should put this logic only in AncillaryCostTable to be called when "Confirm changes" button is clicked
  // This implementation should be done taking into consideration the Rebook Flow, which currently as its own "Confirm changes" button
  const [adjustableSurcharge, setAdjustableSurcharge] = useState<SurchargeType>({});

  const [partialSsrs, setPartialSsrs] = useState<string[]>([]);

  const outboundFlightOnd = rebooking
    ? rebookOffers?.outboundOnd || ''
    : flightDetails?.OrderDetails?.Journeys[0]?.Ond || '';
  const returnFlightOnd = rebooking
    ? rebookOffers?.returnOnd || ''
    : flightDetails?.OrderDetails?.Journeys[1]?.Ond || '';
  const ondArray: string[] = [outboundFlightOnd, returnFlightOnd].filter(str => str !== '').sort(); //TODO

  const [oldAndNewSsrs, setOldAndNewSsrs] = useState<SsrsByPax>(() => {
    return Object.values(flightDetails.OrderDetails.Passengers).reduce((acc, pax) => {
      if (pax.Type === 'INF') {
        return acc;
      }
      const ssrs: SsrDetails[] = [];
      formSsrs(ssrs, pax, flightDetails);

      acc[pax.Id] = mergeBXObjects(ssrs);
      return acc;
    }, {} as SsrsByPax);
  });
  const [bookedSsrs] = useState<SsrsByPax>(oldAndNewSsrs);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showFailure, setShowFailure] = useState<boolean>(false);
  const [, setTableOpen] = useState<boolean>(false);
  const [missingAncillaries, setMissingAncillaries] = useState<MissingAncillaries>({ outbound: [], inbound: [] });
  const [bookedPartially, setBookedPartially] = useState<boolean>(false);
  const [seatWarning, setSeatWarning] = useState<boolean>(false);

  const isOutboundRebooked = rebooking && rebookOffers.outbound.new !== rebookOffers.outbound.original;
  const isInboundRebooked = rebooking && rebookOffers.inbound.new !== rebookOffers.inbound.original;

  const handleCancelButton = () => {
    if (setAncillariesPage && !displayTable) {
      setAncillariesPage(false);
    }
    setDisplayTable(false);
  };

  const handleValidationDialog = () => {
    setValidationDialog(false);
  };

  const handleAmendDialog = () => {
    setBookedPartially(false);
  };

  useEffect(() => {
    setOldAndNewSsrs(prev => {
      const updated: SsrsByPax = {};
      Object.entries(prev).forEach(([key, group]) => {
        updated[key] = group.map(item => ({
          ...item,
          code: updateSsrCode(item.code, outboundRaw ?? inboundRaw),
        }));
      });
      return updated;
    });
  }, [outboundRaw, inboundRaw]);

  const classes = useStyles();

  const { setSearchAncillariesLambdaRequest, ancillaryResponse, ancillaryStatus } = useAncillaryBooking();

  const includedBaggageForPerfect = (boundFare: string | undefined, journeyOnd: string): TFF.Ancillary[] => {
    if (boundFare === 'Perfect') {
      const ancillariesForPerfect: TFF.Ancillary[] = bookedAncillaries
        ? Object.values(bookedAncillaries).filter(a => a.JourneyOnd === journeyOnd)
        : [];

      return ancillariesForPerfect.filter(a => ['BI20', 'BI25', 'BI32'].includes(a.Type));
    }
    return [];
  };

  const bookedAncillaries: TFF.Ancillaries = flightDetails.OrderDetails.Ancillaries;
  const outboundJourneyOnd = rebooking
    ? (newOnds?.Outbound || '').match(/.{1,3}/g)?.join('-') || ''
    : flightDetails.OrderDetails.Journeys[0]?.Ond || '';

  const inboundJourneyOnd = rebooking
    ? (newOnds?.Inbound || '').match(/.{1,3}/g)?.join('-') || ''
    : flightDetails.OrderDetails.Journeys[1]?.Ond || '';
  const outboundFare: string | undefined = fareFamily?.[0];
  const inboundFare: string | undefined = fareFamily?.[1];
  const includedBaggageForPerfectOutbound: TFF.Ancillary[] = includedBaggageForPerfect(
    outboundFare,
    outboundJourneyOnd,
  );
  const includedBaggageForPerfectInbound: TFF.Ancillary[] = includedBaggageForPerfect(inboundFare, inboundJourneyOnd);

  useEffect(() => {
    if (ancillaryStatus === 'FAILURE') {
      return;
    }

    if (ancillaryStatus === 'IN_ANCILLARY') {
      // Checks whether the request comes from rebooking section or ancillary section
      const sourceData = rebooking ? payload : journeys;

      const localeParam: string = localeFormat(locale);

      setSearchAncillariesLambdaRequest({
        journeyIdentifiers: Object.values(sourceData)
          .map(journey => {
            const typedJourney = journey as Journey;
            return {
              journeyIdentifier: typedJourney.JourneyIdentifier!,
              productClass: typedJourney.AdditionalParams?.ProductClass,
              promotionCode: '2019',
            };
          })
          .filter(Boolean),
        currency: currency,
        locale: localeParam,
      });
    }

    if (ancillaryStatus === 'CONFIRMED' && ancillaryResponse?.status === 'SUCCESS') {
      if (flightDetails.OrderDetails.Journeys.length < 2 && ancillaryResponse.body[0].errors) {
        if (ancillaryResponse.body[0].errors.length < 1) {
          /* oneway trip
              directly assigns ancillaries without checking values whether outbound or inbound
            */
          const metaValues = [...ancillaryResponse.body[0].metaValues];
          const ancillaryDirections = ancillaryResponse.body.map(checkDirection);
          const journeysOnd = getJourneysOnd(ancillaryDirections);
          const missingAncillaries = getMissingAncillaries(bookedAncillaries, journeysOnd, metaValues);
          addMissingAncillariesToMetaValues(missingAncillaries.outbound, metaValues, locale, currency);
          setMissingAncillaries(missingAncillaries);

          setOutboundRows(
            Object.values(groupByType(ancillaryResponse.body[0].metaValues)).map((i: AncillariesAccordion) => i),
          );
          setOutboundRaw([...ancillaryResponse.body[0].metaValues]);

          setOutboundRows(prevRows => updateGroupedRows(prevRows, intl, includedBaggageForPerfectOutbound));
        } else {
          setSsrErrors(ancillaryResponse.body[0].errors[0].message);
        }
      } else {
        /* roundtrip
            compares directions whether values belong to outbound or inbound, assigns accordingly
          */
        const metaValues0 = [...ancillaryResponse.body[0].metaValues];
        const metaValues1 = [...ancillaryResponse.body[1].metaValues];
        const errors0 = ancillaryResponse.body[0]?.errors;
        const errors1 = ancillaryResponse.body[1]?.errors;
        const ancillaryDirections = ancillaryResponse.body.map(checkDirection);

        const journeysOnd = getJourneysOnd(ancillaryDirections);
        const missingAncillaries = getMissingAncillaries(
          bookedAncillaries,
          journeysOnd,
          metaValues0,
          metaValues1,
          errors0,
          errors1,
        );
        //Outbound missing ancillaries
        addMissingAncillariesToMetaValues(missingAncillaries.outbound, metaValues0, locale, currency);
        //Inbound missing ancillaries
        addMissingAncillariesToMetaValues(missingAncillaries.inbound, metaValues1, locale, currency);
        setMissingAncillaries(missingAncillaries);

        const metaValues = [metaValues0, metaValues1];
        const firstSegment = ancillaryResponse.body[0].segment.split('-').at(-1);

        journeys.forEach((journey, index) => {
          // The following condition prevents the case when we received more than 2 legs (outbound + inbound)
          // We should not receive more than 2 legs however we can't control what we received from the BE so
          // this condition prevents FE issues to happen because of this
          if (index !== 0 && index !== 1) {
            return;
          }

          if (ancillaryResponse.body.length && ancillaryResponse.body[index].errors?.length) {
            setSsrErrors(ancillaryResponse.body[index].errors![0].message);
          } else {
            const formattedOnd = flightDetails.OrderDetails.Journeys[0].Ond!.replaceAll('-', '');
            const isFirstSegmentMatch = rebooking ? firstSegment === newOnds?.Outbound : formattedOnd === firstSegment;

            const rowSetterMap: { [key: string]: [(rows: any) => void, (rows: any) => void] } = {
              true: [setOutboundRows, setInboundRows],
              false: [setInboundRows, setOutboundRows],
            };

            const setRows = rowSetterMap[String(isFirstSegmentMatch)][index];
            setRows(Object.values(groupByType(metaValues[index])).map(i => i));
          }
        });

        //keep raw response for later to compare values
        setOutboundRaw([...metaValues0]);
        setInboundRaw([...metaValues1]);
        //set modified values to render ancillary accordions
        setOutboundRows(prevRows => updateGroupedRows(prevRows, intl, includedBaggageForPerfectOutbound));
        setInboundRows(prevRows => updateGroupedRows(prevRows, intl, includedBaggageForPerfectInbound)); //will be [] for flights with no inbound
      }
    }
  }, [ancillaryStatus]);

  useEffect(() => {
    setGroupedRows(mergeByType(outboundRows, inboundRows));
  }, [inboundRows, outboundRows]);

  const findSsr = (paxId: string, ssrDetails: SsrDetails) => {
    return oldAndNewSsrs[paxId]?.find(ssr => {
      return ssr.code === ssrDetails.code && ssr.segmentOnd === ssrDetails.segmentOnd;
    });
  };

  const handleConfirm = () => {
    const newSsrsToAdd = getNewAddedSsrs(oldAndNewSsrs, bookedSsrs, rebooking, rebookOffers, ondArray);
    const paxDataToRequest = displayTable ? newSsrsToAdd : oldAndNewSsrs;
    const finalPaxData = generatePaxDataForAmendRequest(
      rebooking,
      paxDataToRequest,
      outboundRaw,
      inboundRaw,
      bookedAncillaries,
      isNskBooking ? adjustableSurcharge : undefined,
      ancillarySeats,
      newOnds,
    );

    setAmendBookingRequest({
      action: displayTable ? 'confirm' : 'validate',
      bookingSource: 'TUI-NSK',
      recordLocator: recordLocator,
      tfmPnr: tfmPnr as RecordLocator,
      paxData: finalPaxData,
      midocoOrderNo: midocoOrderNo,
      sessionId: amendResponse?.sessionId,
    });
  };

  const sendAncillaryMessages = (status: 'Successful' | 'Unsuccessful') => {
    const paxData = amendResponse?.query?.paxData;
    if (!paxData) return;

    const hasSsrChanges = paxData.some(
      p => (p.addSsrs && p.addSsrs.length > 0) || (p.removeSsrIds && p.removeSsrIds.length > 0),
    );
    const hasSeatChanges = paxData.some(
      p => (p.addSeats && p.addSeats.length > 0) || (p.removeSeatIds && p.removeSeatIds.length > 0),
    );

    if (hasSsrChanges) {
      sendMessage('LOG_ENTRY', 'EDIT_SSR', status);
    }
    if (hasSeatChanges) {
      sendMessage('LOG_ENTRY', 'EDIT_ASR', status);
    }
  };

  useEffect(() => {
    if (displayTable && amendStatus === 'SUCCESS') {
      setShowConfirmation(true);
      setShowSuccess(true);
      sendAncillaryMessages('Successful');
    } else if (displayTable && amendStatus === 'FAILURE') {
      setShowConfirmation(true);
      setShowFailure(true);
      sendAncillaryMessages('Unsuccessful');
    }

    if (amendStatus === 'SUCCESS') {
      if (amendResponse?.tfaAmendResponseDetails?.status === 'BOOKED_PARTIALLY') {
        const hasSeatCountDiffer = amendResponse.tfaAmendResponseDetails.issues?.some(issue =>
          issue.message?.includes('SEAT_COUNT_DIFFER'),
        );
        if (hasSeatCountDiffer) {
          setSeatWarning(true);
          setDisplayTable(false);
          if (setShowCostTable) {
            setShowCostTable(false);
          }
          return;
        }
      }

      setDisplayTable(true);
      if (amendResponse?.tfaAmendResponseDetails?.validatedSsrsAmendResult?.status === 'NOT_AVAILABLE') {
        setDisplayTable(false);

        const ssrCodes = amendResponse.tfaAmendResponseDetails.validatedSsrsAmendResult?.ancillaries
          .filter(a => a.status !== 'AVAILABLE')
          .map(a => `${a.segmentOnd}~${a.paxId}~${a.code}`);

        setPartialSsrs(ssrCodes);
        setBookedPartially(true);
      }

      if (amendResponse && setAncillaryId) {
        setAncillaryId(amendResponse.query.sessionId!);
      }
    } else if (amendStatus === 'FAILURE') {
      setValidationDialog(true);
      if (setShowCostTable) {
        setShowCostTable(false);
      }
    }
  }, [amendStatus]);

  useEffect(() => {
    if (rebooking && showCostTable) {
      setAmendBookingRequest({
        action: 'validate',
        bookingSource: 'TUI-NSK',
        recordLocator: recordLocator,
        tfmPnr: tfmPnr as RecordLocator,
        paxData: generatePaxDataForAmendRequest(
          rebooking,
          oldAndNewSsrs,
          outboundRaw,
          inboundRaw,
          bookedAncillaries,
          undefined,
          ancillarySeats,
          newOnds,
        ),
        midocoOrderNo: midocoOrderNo,
        sessionId: sessionId,
      });
    }
  }, [showCostTable]);

  useEffect(() => {
    if (rebooking && ancillaryRequest) {
      if (handleAmendBookingConfirm) {
        const newSsrsToAdd = getNewAddedSsrs(oldAndNewSsrs, bookedSsrs, rebooking, rebookOffers, ondArray);
        handleAmendBookingConfirm(
          generatePaxDataForAmendRequest(
            rebooking,
            newSsrsToAdd,
            outboundRaw,
            inboundRaw,
            bookedAncillaries,
            adjustableSurcharge,
            ancillarySeats,
            newOnds,
          ),
          parseInt(adjustableSurcharge['Service Fee'] ?? '0'),
        );
      }
    }
  }, [ancillaryRequest]);

  const isTypeValid = type => {
    return ['HAND_LUGGAGE', 'BAGGAGE', 'SPORTS', 'ANIMALS'].includes(type);
  };

  const renderError = () => {
    if (outboundRows && outboundRows.length > 0) {
      return;
    } else if (inboundRows && inboundRows.length > 0) {
      return;
    } else {
      return <Alert severity="error">{ssrErrors}</Alert>;
    }
  };

  return (
    <>
      <TFFDialog
        dialogId="seat-warning-dialog"
        isOpen={seatWarning}
        confirmButton
        confirmButtonText="OK"
        onConfirm={() => setSeatWarning(false)}
        header={<TFFTypography text="Warning" fontWeight="bold" variant="lead" />}
        children={
          <TFFTypography
            text="Some seats are not available on the new flight and will be lost when confirmed. To retain them select new seats from the seat map"
            color="primary"
          />
        }
      />

      {!rebooking && (
        <Box width="100%" className={classes.ancillaryHeader}>
          <Typography fontSize="27px" fontWeight={700} color="primary">
            {intl.formatMessage({ id: 'ancillaries.title' })}
          </Typography>
        </Box>
      )}

      {!displayTable &&
        [...groupedRows]
          .sort((a, b) => {
            const order = ['HAND_LUGGAGE', 'BAGGAGE', 'SPORTS', 'ANIMALS'];
            return order.indexOf(a.type) - order.indexOf(b.type);
          })
          .map(
            (ancillary, index) =>
              isTypeValid(ancillary.type) && (
                <AncillaryAccordion
                  key={ancillary.type}
                  groupedRows={ancillary}
                  flightDetails={flightDetails}
                  addSsrsToAdd={addSsrsToAdd}
                  removeSsrsToAdd={removeSsrsToAdd}
                  resetSsrs={resetSsrs}
                  findSsr={findSsr}
                  ssrsToAdd={oldAndNewSsrs}
                  rebooking={rebooking}
                  fareFamily={fareFamily}
                  newOnds={newOnds}
                  setSsrsToAdd={setOldAndNewSsrs}
                  groupedAncillary={groupedRows}
                  includedSsrs={bookedSsrs}
                  validatedAncillaries={amendResponse?.tfaAmendResponseDetails?.validatedSsrsAmendResult?.ancillaries}
                  missingAncillaries={missingAncillaries}
                  isOutboundRebooked={isOutboundRebooked}
                  isInboundRebooked={isInboundRebooked}
                />
              ),
          )}

      <Box>{renderError()}</Box>

      {displayTable && (
        <>
          <AncillaryCostTable
            flightDetails={flightDetails}
            newAddedSsrs={getNewAddedSsrs(oldAndNewSsrs, bookedSsrs, rebooking, rebookOffers, ondArray)}
            includedSsrs={bookedSsrs}
            outboundMetaValues={outboundRaw}
            inboundMetaValues={inboundRaw}
            rebooking={rebooking}
            rebookOffers={rebookOffers}
            setDisplayTable={setDisplayTable}
            submitChanges={handleConfirm}
            setAdjustableSurcharge={setAdjustableSurcharge}
            amendBalanceDue={amendBalanceDue}
            outboundFlightOnd={outboundFlightOnd}
            returnFlightOnd={returnFlightOnd}
            isNskBooking={isNskBooking}
          />
          <CostOverview
            ssrDisplayData={{
              ssrsToAdd: oldAndNewSsrs,
              includedSsrs: bookedSsrs,
              metaValues: [...new Set([...inboundRows, ...outboundRows])],
            }}
          />
        </>
      )}

      {showConfirmation && (displayTable || displayRebookConfirmation) && (
        <ConfirmationDialog
          showConfirmation={showConfirmation}
          showFailure={showFailure}
          showSuccess={showSuccess}
          setShowConfirmation={setShowConfirmation}
          setTableOpen={setTableOpen}
          rebooking={rebooking}
          flightDetails={flightDetails}
          rebookingResponse={amendResponse}
        />
      )}

      {bookedPartially && <AmendDialog handleClose={handleAmendDialog} open={bookedPartially} ssrs={partialSsrs} />}

      {validationDialog && <ValidationDialog handleClose={handleValidationDialog} open={validationDialog} />}

      {!rebooking && !displayTable && (
        <Box className={classes.ancillaryFooter}>
          <Button
            color="info"
            className={classes.ancillaryCancel}
            onClick={() => {
              handleCancelButton();
            }}
          >
            {intl.formatMessage({ id: 'ancillaries.action.cancel' })}
          </Button>
          <Button
            className={classes.ancillaryAccept}
            onClick={() => {
              handleConfirm();
            }}
          >
            {intl.formatMessage({ id: 'ancillaries.action.accept' })}
          </Button>
        </Box>
      )}
    </>
  );
};

export default AncillariesPage;
